import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { notScanned, scanError, useOverview, useStartScan } from '../components/site.js';
import { Button, LinkButton, LoadError, Loading, Notice, TableBox } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime } from '../i18n.js';
import { useMe } from '../session.js';

type CrawlLink = Schemas['CrawlLink'];
type View = 'broken' | 'redirected' | 'internal' | 'external' | 'images';

const active = (scan: Schemas['Scan'] | undefined) =>
  scan?.status === 'queued' || scan?.status === 'running';

/**
 * An image of an unconfirmed site comes by its host only, «https://praxis.de/…» (SP-A2.15,
 * `resourceHost` in packages/rules): its path names plugins and libraries.
 */
const hiddenPath = (url: string) => url.endsWith('/…');

/** "/kontakt" for a page of the site, the host and path for another site. */
function shortUrl(url: string, internal: boolean) {
  try {
    const u = new URL(url);
    // «/…», not the encoded «/%E2%80%A6».
    const path = hiddenPath(url) ? '/…' : `${u.pathname}${u.search}`;
    return internal ? path : `${u.host}${path === '/' ? '' : path}`;
  } catch {
    return url;
  }
}

/** One target with every page it was found on. */
interface Grouped {
  readonly link: CrawlLink;
  readonly sources: string[];
}

function group(items: readonly CrawlLink[]): Grouped[] {
  const byTarget = new Map<number, Grouped>();
  for (const l of items) {
    // By the key of the API: an address shown by its host only cannot tell targets apart.
    const found = byTarget.get(l.targetKey);
    if (found) {
      if (!found.sources.includes(l.sourceUrl)) found.sources.push(l.sourceUrl);
    } else byTarget.set(l.targetKey, { link: l, sources: [l.sourceUrl] });
  }
  return [...byTarget.values()];
}

/** The short error on the badge: the HTTP status or what the connection said. */
function badge(link: CrawlLink, t: TFunction) {
  if (link.httpStatus !== null) return String(link.httpStatus);
  return t(`links.badge.${link.errorCode ?? 'unknown'}`, {
    defaultValue: t('links.badge.unknown'),
  });
}

/** What to do about it, as a key of links.suggestions. */
function suggestion(link: CrawlLink): string {
  const status = link.httpStatus;
  if (link.errorCode === 'DNS_NXDOMAIN') return 'remove';
  if (status !== null && status >= 500) return 'server';
  if (status === 404 || status === 410) {
    if (link.kind === 'img') return 'image';
    if (link.kind !== 'a') return 'resource';
    return link.isInternal ? 'internal' : 'external';
  }
  return 'later';
}

/** The problem card behind a link, with its ready-made fix (T-72). */
function ruleOf(link: CrawlLink) {
  if (link.kind === 'img') return 'LINK_BROKEN_IMAGE';
  if (link.kind !== 'a') return 'LINK_BROKEN_RESOURCE';
  return link.isInternal ? 'LINK_BROKEN_INTERNAL' : 'LINK_BROKEN_EXTERNAL';
}

/** A failed crawl checked no link: why, and what the customer can do about it (SP-A2.48). */
function FailedCrawl({
  code,
  siteId,
  botSetup,
}: {
  code: string | null | undefined;
  siteId: string;
  /** The overview shows how to let the check in: its own check sees the bot protection now. */
  botSetup: boolean;
}) {
  const { t, i18n } = useTranslation();
  // A bot protection or a password hides the site: a setup, not a failure (T-34, PRD-01).
  if (code === 'BOT_PROTECTION' || code === 'AUTH_REQUIRED') {
    const bot = code === 'BOT_PROTECTION';
    return (
      <Notice
        kind="warning"
        title={t('links.failedNotice.title')}
        action={
          // The way there only where the overview shows the steps (SetupPanel).
          bot && !botSetup ? undefined : (
            <LinkButton
              to={bot ? `/sites/${siteId}` : `/sites/${siteId}/settings#basic-auth`}
              variant="primary"
              size="sm"
            >
              {bot ? t('links.failedNotice.botAction') : t('setup.auth.addPassword')}
            </LinkButton>
          )
        }
      >
        {bot ? t('links.failedNotice.bot') : t('setup.auth.text')}
      </Notice>
    );
  }
  const text =
    code === 'ROBOTS_DISALLOW_ALL'
      ? t('links.failedNotice.robots')
      : // The start page did not come in: its HTTP status or what the connection said.
        code && i18n.exists(`links.errors.${code}`)
        ? t('links.failedNotice.home', { reason: t(`links.errors.${code}`) })
        : t('links.failedNotice.other');
  return (
    <Notice kind="warning" title={t('links.failedNotice.title')}>
      {text}
    </Notice>
  );
}

/** Shares of the bar, coloured as the legend (site.css .segbar). */
const BAR = ['ok', 'redirected', 'broken', 'unknown'] as const;

/** Links of the latest crawl (T-100, site-links.html). */
export function Links() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const [view, setView] = useState<View>('broken');
  const titleId = useId();
  const scans = useQuery({
    queryKey: ['site', siteId, 'scans', 'crawl'],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/scans', {
          params: { path: { siteId }, query: { type: 'crawl', limit: 5 } },
        }),
      ),
    refetchInterval: (query) => (active(query.state.data?.items[0]) ? 5000 : false),
  });
  const running = scans.data?.items.find(active);
  // A crawl closed because the site was paused crawled nothing: the one before it still counts.
  const latest = scans.data?.items.find((s) => !active(s) && !notScanned(s));
  const failed = latest?.status === 'failed';
  const links = useQuery({
    queryKey: ['site', siteId, 'links', latest?.id],
    queryFn: () =>
      unwrap(
        api.GET('/scans/{scanId}/links', {
          params: { path: { scanId: latest?.id ?? '' }, query: { broken: 'true', limit: 200 } },
        }),
      ),
    // A failed crawl stored no links: nothing to ask (SP-A2.48).
    enabled: Boolean(latest) && !failed,
  });
  // Redirects only when asked for: the page itself needs one list.
  const redirects = useQuery({
    queryKey: ['site', siteId, 'links', latest?.id, 'redirected'],
    queryFn: () =>
      unwrap(
        api.GET('/scans/{scanId}/links', {
          params: {
            path: { scanId: latest?.id ?? '' },
            query: { redirected: 'true', limit: 200 },
          },
        }),
      ),
    enabled: Boolean(latest) && view === 'redirected',
  });
  const start = useStartScan(siteId, 'crawl');
  const summary = links.data?.summary;
  const broken = group(links.data?.items ?? []);
  const subsets: Record<'internal' | 'external' | 'images', Grouped[]> = {
    internal: broken.filter((b) => b.link.isInternal && b.link.kind === 'a'),
    external: broken.filter((b) => !b.link.isInternal && b.link.kind === 'a'),
    images: broken.filter((b) => b.link.kind === 'img'),
  };
  const shown = view === 'broken' ? broken : view === 'redirected' ? [] : subsets[view];
  const source = (url: string) => {
    const path = shortUrl(url, true);
    return path === '/' ? t('links.homePage') : path;
  };
  const chips: [View, number | null][] = [
    ['broken', summary?.broken ?? null],
    ['redirected', summary?.redirected ?? null],
    ['internal', subsets.internal.length],
    ['external', subsets.external.length],
    ['images', subsets.images.length],
  ];
  const forbidden = start.error instanceof ApiError && start.error.code === 'FORBIDDEN';
  // Before the ownership is confirmed only the planned crawl runs (site-links.html: unverified).
  const overview = useOverview(siteId).data;
  const unverified = overview?.site.verified === false;

  return (
    <>
      <div className="hd">
        <div>
          <h1>{t('links.title')}</h1>
          {latest ? (
            <div className="sub">
              <span>
                {t('links.lastRun', {
                  date: formatDateTime(
                    latest.finishedAt ?? latest.createdAt,
                    i18n.language,
                    timeZone,
                  ),
                  count: latest.pagesCrawled ?? 0,
                })}
              </span>
              {latest.status !== 'completed' ? (
                <span>{t(`links.scanStatus.${latest.status}`)}</span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="acts">
          <Button
            size="sm"
            busy={start.isPending}
            disabled={Boolean(running)}
            onClick={() => start.mutate()}
          >
            {t('links.recheck')}
          </Button>
          {/* The dashboard's tile leads here; the area's problems one step on (decision 11). */}
          <LinkButton to={`/sites/${siteId}/area/links`} variant="ghost" size="sm">
            {t('site.tiles.more')}
          </LinkButton>
        </div>
      </div>
      {unverified || forbidden ? (
        <Notice
          kind="warning"
          title={t('links.unverified.title')}
          action={
            <LinkButton to={`/sites/${siteId}/settings#verify`} variant="primary" size="sm">
              {t('scan.toSettings')}
            </LinkButton>
          }
        >
          {t('links.unverified.text')}
        </Notice>
      ) : start.isError ? (
        <Notice kind="error">{scanError(start.error, t)}</Notice>
      ) : null}
      {running ? <Notice kind="info">{t('links.running')}</Notice> : null}
      {scans.isPending ? <Loading /> : null}
      {scans.isError ? (
        <LoadError message={errorMessage(scans.error, t)} onRetry={() => void scans.refetch()} />
      ) : null}
      {!latest && scans.data && !running ? (
        <div className="empty">
          <b>{t('links.none')}</b>
        </div>
      ) : null}
      {links.isLoading ? <Loading /> : null}
      {links.isError ? (
        <LoadError message={errorMessage(links.error, t)} onRetry={() => void links.refetch()} />
      ) : null}

      {/* A failed crawl checked no link: it says why, without a headline (SP-A2.48). */}
      {failed ? (
        <FailedCrawl
          code={latest.errorCode}
          siteId={siteId}
          botSetup={overview?.access.block === 'bot_protection'}
        />
      ) : summary && summary.total > 0 ? (
        <section className="card" aria-labelledby={titleId}>
          <h2 id={titleId}>
            {summary.broken > 0
              ? t('links.headline', { broken: summary.broken, count: summary.total })
              : t('links.allFine', { count: summary.total })}{' '}
            <small>{t('links.scope', { count: latest?.pagesCrawled ?? 0 })}</small>
          </h2>
          <div
            className="segbar"
            role="img"
            aria-label={BAR.map((k) => t(`links.bar.${k}`, { count: summary[k] })).join(', ')}
          >
            {BAR.map((k) =>
              summary[k] > 0 ? <i key={k} className={k} style={{ flexGrow: summary[k] }} /> : null,
            )}
          </div>
          <div className="legend">
            {BAR.map((k) =>
              summary[k] > 0 ? (
                <span key={k}>
                  <i className={k} aria-hidden="true" />
                  {t(`links.bar.${k}`, { count: summary[k] })}
                </span>
              ) : null,
            )}
          </div>
          <div className="chips" role="group" aria-label={t('links.filter')}>
            {chips.map(([k, n]) => (
              <button key={k} type="button" aria-pressed={view === k} onClick={() => setView(k)}>
                <span>{t(`links.views.${k}`)}</span>
                {n !== null ? (
                  <>
                    {' '}
                    <span className="n">{n}</span>
                  </>
                ) : null}
              </button>
            ))}
          </div>

          {view === 'redirected' ? (
            <RedirectTable
              rows={redirects.data ? group(redirects.data.items) : null}
              error={redirects.isError ? redirects.error : null}
              source={source}
              onRetry={() => void redirects.refetch()}
            />
          ) : shown.length === 0 ? (
            <div className="empty">
              <b>{view === 'broken' ? t('links.noBroken') : t('links.noneHere')}</b>
            </div>
          ) : (
            // Wider than a phone: it scrolls in its frame, which takes the keyboard focus (axe).
            <TableBox label={t('links.broken', { count: shown.length })}>
              <table>
                <caption className="sr-only">{t('links.broken', { count: shown.length })}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('links.columns.link')}</th>
                    <th scope="col">{t('links.columns.foundOn')}</th>
                    <th scope="col">{t('links.columns.error')}</th>
                    <th scope="col">{t('links.columns.suggestion')}</th>
                    <th scope="col">{t('links.columns.fix')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(({ link, sources }) => (
                    <tr key={link.id}>
                      <td>
                        <span className="spec">
                          {t(`links.kinds.${link.kind}`)} ·{' '}
                          {link.isInternal ? t('links.internal') : t('links.external')}
                        </span>
                        <br />
                        <span className="url">{shortUrl(link.targetUrl, link.isInternal)}</span>
                        {link.anchorText ? (
                          <>
                            <br />
                            <span className="spec">
                              {t('links.anchor', { text: link.anchorText })}
                            </span>
                          </>
                        ) : null}
                      </td>
                      <td>
                        <Sources sources={sources} source={source} />
                      </td>
                      <td>
                        <span
                          className="badge bad nodot"
                          title={
                            link.httpStatus === null
                              ? t(`links.errors.${link.errorCode ?? 'unknown'}`, {
                                  defaultValue: t('links.errors.unknown'),
                                })
                              : undefined
                          }
                        >
                          {badge(link, t)}
                        </span>
                      </td>
                      <td>{t(`links.suggestions.${suggestion(link)}`)}</td>
                      <td>
                        <LinkButton
                          to={`/sites/${siteId}/findings?open=${ruleOf(link)}`}
                          variant="secondary"
                          size="sm"
                        >
                          {t('links.fix')}
                        </LinkButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableBox>
          )}
        </section>
      ) : null}
    </>
  );
}

/** Up to three source pages, then how many more. */
function Sources({ sources, source }: { sources: string[]; source: (url: string) => string }) {
  const { t } = useTranslation();
  return (
    <>
      {sources.slice(0, 3).map(source).join(', ')}
      {sources.length > 3 ? ` ${t('links.morePages', { count: sources.length - 3 })}` : ''}
    </>
  );
}

/** Working links that answer only after a redirect: worth pointing straight at the target. */
function RedirectTable({
  rows,
  error,
  source,
  onRetry,
}: {
  rows: Grouped[] | null;
  error: unknown;
  source: (url: string) => string;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  if (error) return <LoadError message={errorMessage(error, t)} onRetry={onRetry} />;
  if (!rows) return <Loading />;
  if (rows.length === 0) return <p className="bodyM">{t('links.noRedirects')}</p>;
  return (
    <>
      <p className="bodyM">{t('links.redirectHint')}</p>
      <TableBox label={t('links.views.redirected')}>
        <table>
          <caption className="sr-only">{t('links.views.redirected')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('links.columns.link')}</th>
              <th scope="col">{t('links.columns.foundOn')}</th>
              <th scope="col">{t('links.columns.redirects')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ link, sources }) => (
              <tr key={link.id}>
                <td className="url">{shortUrl(link.targetUrl, link.isInternal)}</td>
                <td>
                  <Sources sources={sources} source={source} />
                </td>
                <td className="num">{t('links.hops', { count: link.redirectHops })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableBox>
    </>
  );
}
